
import { useState } from 'react';
import { useAuth } from '@/contexts/auth';
import { useCountry } from '@/contexts/country';
import { PointsService } from '@/services/points/PointsService';
import { toast } from 'sonner';

export const usePointsIntegration = () => {
  const { user } = useAuth();
  const { countryCode } = useCountry();
  const [isAwarding, setIsAwarding] = useState(false);

  const awardPointsForPollVote = async (pollId: string, pollCountryCode?: string) => {
    console.log('POINTS_INTEGRATION: ===== STARTING POINTS AWARD PROCESS =====');
    console.log('POINTS_INTEGRATION: Function called with parameters:', {
      pollId,
      pollIdType: typeof pollId,
      pollIdLength: pollId?.length,
      pollCountryCode,
      userId: user?.id,
      contextCountryCode: countryCode
    });

    if (!user?.id) {
      console.log('POINTS_INTEGRATION: ERROR - Missing user ID, cannot award points');
      return;
    }

    console.log('POINTS_INTEGRATION: User ID confirmed:', user.id);

    // FIXED: Validate pollId is a valid UUID string format
    if (!pollId || typeof pollId !== 'string') {
      console.log('POINTS_INTEGRATION: ERROR - Invalid poll ID format:', pollId);
      return;
    }

    // Additional validation for UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(pollId)) {
      console.log('POINTS_INTEGRATION: ERROR - Poll ID is not a valid UUID format:', pollId);
      return;
    }

    console.log('POINTS_INTEGRATION: Poll ID validated as UUID:', pollId);

    // Determine the correct country code to use for points awarding
    let targetCountryCode = pollCountryCode || countryCode;
    console.log('POINTS_INTEGRATION: Initial target country code:', targetCountryCode);
    
    // If we don't have a country code from either source, try to fetch from poll
    if (!targetCountryCode) {
      console.log('POINTS_INTEGRATION: No country code available, fetching from poll database');
      try {
        const fetchedCountryCode = await PointsService.getPollCountryCode(pollId);
        targetCountryCode = fetchedCountryCode || undefined;
        console.log('POINTS_INTEGRATION: Fetched country code from database:', fetchedCountryCode);
      } catch (fetchError) {
        console.error('POINTS_INTEGRATION: Error fetching country code from poll:', fetchError);
      }
    }

    // If poll has a specific country code that differs from context, use poll's country code
    if (pollCountryCode && pollCountryCode !== countryCode) {
      console.log('POINTS_INTEGRATION: Using poll-specific country code instead of context:', {
        pollCountryCode,
        contextCountryCode: countryCode
      });
      targetCountryCode = pollCountryCode;
    }

    if (!targetCountryCode) {
      console.log('POINTS_INTEGRATION: ERROR - No country code available from any source, cannot award points');
      return;
    }

    console.log('POINTS_INTEGRATION: Final country code for points award:', targetCountryCode);
    
    setIsAwarding(true);
    console.log('POINTS_INTEGRATION: Set isAwarding to true');
    
    try {
      console.log('POINTS_INTEGRATION: Checking if poll has points enabled...');

      // Check if poll has points enabled
      let pollPointsEnabled;
      try {
        pollPointsEnabled = await PointsService.isPollPointsEnabled(pollId);
        console.log('POINTS_INTEGRATION: Poll points enabled check result:', pollPointsEnabled);
      } catch (enabledCheckError) {
        console.error('POINTS_INTEGRATION: Error checking if poll points enabled:', enabledCheckError);
        return;
      }
      
      if (!pollPointsEnabled) {
        console.log('POINTS_INTEGRATION: Poll does not have points enabled - skipping points award');
        return;
      }

      console.log('POINTS_INTEGRATION: Poll has points enabled, proceeding with award');
      console.log('POINTS_INTEGRATION: Calling PointsService.awardPollPoints with:', {
        userId: user.id,
        pollId,
        pollIdType: typeof pollId,
        targetCountryCode
      });

      let result;
      try {
        // FIXED: Ensure pollId is passed as string (it already is, but being explicit)
        result = await PointsService.awardPollPoints(user.id, String(pollId), targetCountryCode);
        console.log('POINTS_INTEGRATION: PointsService.awardPollPoints completed with result:', result);
      } catch (awardError) {
        console.error('POINTS_INTEGRATION: Error in PointsService.awardPollPoints:', awardError);
        console.error('POINTS_INTEGRATION: Award error stack:', awardError instanceof Error ? awardError.stack : 'No stack trace');
        
        // Enhanced error logging for debugging
        if (awardError && typeof awardError === 'object') {
          console.error('POINTS_INTEGRATION: Award error details:', {
            message: (awardError as any).message,
            code: (awardError as any).code,
            details: (awardError as any).details,
            hint: (awardError as any).hint
          });
        }
        
        throw awardError; // Re-throw to be caught by outer try-catch
      }
      
      if (result.success && result.points && result.points > 0) {
        console.log('POINTS_INTEGRATION: Successfully awarded points:', result.points);
        toast.success(`You earned ${result.points} points for voting!`, {
          description: `Points added to your ${targetCountryCode} account`
        });
      } else if (result.error) {
        console.log('POINTS_INTEGRATION: Points award failed with error:', result.error);
        // Only show specific error messages for verification issues
        if (result.error.includes('not verified')) {
          console.log('POINTS_INTEGRATION: User not verified - showing verification prompt');
          toast.info('Complete identity verification to earn points', {
            description: `Verify your identity for ${targetCountryCode} to start earning points`
          });
        } else if (result.error.includes('Monthly limit')) {
          console.log('POINTS_INTEGRATION: Monthly limit reached - showing limit message');
          toast.info('Monthly points limit reached', {
            description: result.error
          });
        } else {
          console.log('POINTS_INTEGRATION: Other error, not showing toast:', result.error);
        }
      } else {
        console.log('POINTS_INTEGRATION: No points awarded - result indicates success but no points');
        console.log('POINTS_INTEGRATION: Full result object:', JSON.stringify(result, null, 2));
      }
    } catch (error) {
      console.error('POINTS_INTEGRATION: CRITICAL ERROR in awardPointsForPollVote:', error);
      console.error('POINTS_INTEGRATION: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        type: typeof error,
        errorObject: error
      });
      
      // Enhanced error logging for 406 errors specifically
      if (error && typeof error === 'object' && (error as any).code === '406') {
        console.error('POINTS_INTEGRATION: 406 Error - Not Acceptable:', {
          message: (error as any).message,
          details: (error as any).details,
          hint: (error as any).hint,
          pollId,
          pollIdType: typeof pollId,
          userId: user.id,
          targetCountryCode
        });
      }
      
      // Don't show error toast for points - it's a bonus feature
    } finally {
      console.log('POINTS_INTEGRATION: Cleaning up, setting isAwarding to false');
      setIsAwarding(false);
      console.log('POINTS_INTEGRATION: ===== POINTS AWARD PROCESS COMPLETED =====');
    }
  };

  const createPointsAccountForVerification = async (userId: string, countryCode: string) => {
    console.log('POINTS_INTEGRATION: Creating points account for verification:', {
      userId,
      countryCode
    });

    try {
      const result = await PointsService.createUserPointsForCountry(userId, countryCode);
      
      if (result.success) {
        console.log('POINTS_INTEGRATION: Successfully created points account');
        toast.success(`Points account created for ${countryCode}!`, {
          description: 'You can now start earning points by participating in polls'
        });
      } else {
        console.log('POINTS_INTEGRATION: Error creating points account:', result.error);
      }

      return result;
    } catch (error) {
      console.error('POINTS_INTEGRATION: Error creating points account:', error);
      return { success: false, error: 'Failed to create points account' };
    }
  };

  console.log('POINTS_INTEGRATION: Hook initialized, returning functions:', {
    awardPointsForPollVote: typeof awardPointsForPollVote,
    createPointsAccountForVerification: typeof createPointsAccountForVerification,
    isAwarding
  });

  return {
    awardPointsForPollVote,
    createPointsAccountForVerification,
    isAwarding
  };
};
